/**
 * FP3-033 — Student Goal Sheet (Print).
 *
 * One-student goal sheet for IEP meetings and provider planning.
 * Reads via the existing authenticated `GET /api/students/{id}`
 * endpoint, which already returns goals with `practice_example`. No
 * new server route is needed.
 *
 * Privacy: this page renders ONLY what the API returns for the
 * current user — admin_only docs / private DMs etc. never make it
 * into the goals payload to begin with.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(0)}%`;
}

function StudentHeader({ student }) {
  const provider = student.provider_details?.[0];
  const services = (student.service_areas || []).join(", ");
  const grade = student.grade ? `Grade ${student.grade}` : "";
  return (
    <div className="fp-print-section">
      <div className="fp-print-grid-2">
        <div>
          <div className="fp-print-section-label">Student</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {student.first_name} {student.last_name}
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Readable ID</span>
            <span className="fp-print-kv-val">{student.readable_id || "—"}</span>
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Grade · School</span>
            <span className="fp-print-kv-val">
              {[grade, student.school_name].filter(Boolean).join(" · ") || "—"}
            </span>
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Service Areas</span>
            <span className="fp-print-kv-val">{services || "—"}</span>
          </div>
        </div>
        <div>
          <div className="fp-print-section-label">Provider</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {provider
              ? `${provider.first_name || ""} ${provider.last_name || ""}`.trim()
              : "—"}
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Role</span>
            <span className="fp-print-kv-val">
              {provider?.role_label || provider?.discipline || "—"}
            </span>
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Service Type</span>
            <span className="fp-print-kv-val">
              {student.service_type || "—"}
            </span>
          </div>
          <div className="fp-print-kv-row">
            <span className="fp-print-kv-key">Generated</span>
            <span className="fp-print-kv-val">
              {new Date().toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoalSection({ goal }) {
  return (
    <div
      className="fp-print-section"
      data-testid={`goal-print-${goal.id}`}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        <span className="fp-print-section-title">Goal</span>
        {goal.goal_area && (
          <span className="fp-print-pill" data-testid={`goal-area-${goal.id}`}>
            {goal.goal_area}
          </span>
        )}
        <span className="fp-print-pill">{goal.cadence || "weekly"}</span>
      </div>
      <p
        data-testid={`goal-text-${goal.id}`}
        style={{ margin: "4px 0 10px", fontSize: 13, lineHeight: 1.5 }}
      >
        {goal.goal_text || "—"}
      </p>
      <div className="fp-print-grid-4">
        <div>
          <div className="fp-print-section-label">Baseline</div>
          <div style={{ fontWeight: 600 }}>{fmtPct(goal.baseline_mastery)}</div>
        </div>
        <div>
          <div className="fp-print-section-label">Target</div>
          <div
            data-testid={`goal-target-${goal.id}`}
            style={{ fontWeight: 600 }}
          >
            {fmtPct(goal.target_mastery)}
          </div>
        </div>
        <div>
          <div className="fp-print-section-label">Current</div>
          <div
            data-testid={`goal-current-${goal.id}`}
            style={{ fontWeight: 600 }}
          >
            {fmtPct(goal.current_mastery)}
          </div>
        </div>
        <div>
          <div className="fp-print-section-label">Status</div>
          <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
            {goal.status || "active"}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <div className="fp-print-section-label">
          What this goal looks like in practice
        </div>
        <p
          data-testid={`goal-practice-${goal.id}`}
          style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "var(--fp-print-muted)" }}
        >
          {goal.practice_example ||
            "No practice example added yet."}
        </p>
      </div>
    </div>
  );
}

export default function PrintStudentGoals() {
  const { studentId } = useParams();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getStudent(studentId)
      .then((r) => {
        if (cancelled) return;
        setStudent(r.data);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const goals = (student?.goals || []).filter(
    (g) => (g.status || "active") === "active"
  );
  const name = student
    ? `${student.first_name} ${student.last_name}`
    : "Student";

  return (
    <PrintShell
      title="Student Goal Sheet"
      subtitle={`${name}${student?.readable_id ? ` · ${student.readable_id}` : ""}`}
      testId="print-student-goals"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && (
          <>
            <StudentHeader student={student} />
            <div
              className="fp-print-section-title"
              style={{ marginTop: 6, marginBottom: 8 }}
              data-testid="goals-count-label"
            >
              Active Goals ({goals.length})
            </div>
            {goals.length === 0 ? (
              <div className="fp-print-empty">
                No active goals on file for this student.
              </div>
            ) : (
              goals.map((g) => <GoalSection key={g.id} goal={g} />)
            )}
          </>
        )}
      </PrintAccessGate>
    </PrintShell>
  );
}
